import axios, { AxiosError, type AxiosRequestConfig, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

/**
 * 공통 API 클라이언트 설정
 * 모든 API 요청에 공통으로 적용되는 설정 및 인터셉터
 */

// 재시도 플래그 (무한 루프 방지)
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

// 큐에 있는 요청 처리
const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * 공통 axios 인스턴스 생성
 */
export const apiClient = axios.create({
  baseURL: '/api',
  timeout: 30000,
  withCredentials: true, // 세션 쿠키를 포함하여 전송
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * 요청 인터셉터
 * 모든 요청 전에 실행되는 로직
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 세션 쿠키는 withCredentials로 자동 전송되므로 추가 헤더 불필요
    // 필요시 여기에 인증 토큰 등을 추가할 수 있음
    
    // 쿠키 확인 (디버깅용)
    if (import.meta.env.DEV) {
      const cookies = document.cookie;
      const hasSessionCookie = cookies.includes('PHPSESSID');
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
      console.log(`[API Request] 쿠키 확인: ${hasSessionCookie ? '✅ 세션 쿠키 있음' : '❌ 세션 쿠키 없음'}`);
      if (!hasSessionCookie) {
        console.warn('[API Request] 세션 쿠키가 없습니다. 로그인이 필요할 수 있습니다.');
      }
    }
    
    // withCredentials 확인
    if (!config.withCredentials) {
      config.withCredentials = true;
    }
    
    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * 응답 인터셉터
 * 모든 응답 후에 실행되는 로직
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // 성공 응답 처리
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 401 Unauthorized 오류 처리
    if (error.response?.status === 401) {
      // 디버깅 정보 출력
      console.error('[API] 401 Unauthorized 오류 발생');
      console.error('[API] 요청 URL:', originalRequest.url);
      console.error('[API] 응답 데이터:', error.response?.data);
      console.error('[API] 쿠키 상태:', document.cookie);
      
      // 이미 재시도 중인 경우 큐에 추가
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // 세션 기반 인증이므로 세션 확인 API 호출
        // 또는 로그인 페이지로 리다이렉트
        const isIframe = window.self !== window.top;
        
        // 쿠키 확인
        const hasSessionCookie = document.cookie.includes('PHPSESSID');
        if (!hasSessionCookie) {
          console.warn('[API] 세션 쿠키가 없습니다. 로그인이 필요합니다.');
        }
        
        if (isIframe) {
          // iframe 내부인 경우 부모 창에 메시지 전송
          try {
            window.parent.postMessage(
              {
                type: 'AUTH_REQUIRED',
                message: '로그인이 필요합니다.',
                timestamp: Date.now(),
                hasSessionCookie: hasSessionCookie,
              },
              window.location.origin
            );
            console.warn('[API] 인증이 필요합니다. 부모 창에 메시지를 전송했습니다.');
          } catch (postMessageError) {
            console.error('[API] postMessage 전송 실패:', postMessageError);
            // postMessage 실패 시 직접 리다이렉트 시도
            const loginUrl = '/pages/login.php';
            const currentUrl = window.location.pathname + window.location.search;
            window.top.location.href = `${loginUrl}?redirect=${encodeURIComponent(currentUrl)}`;
          }
        } else {
          // 일반 페이지인 경우 로그인 페이지로 리다이렉트
          const loginUrl = '/pages/login.php';
          const currentUrl = window.location.pathname + window.location.search;
          
          // 사용자에게 알림 (선택적)
          if (confirm('로그인이 필요합니다. 로그인 페이지로 이동하시겠습니까?')) {
            window.location.href = `${loginUrl}?redirect=${encodeURIComponent(currentUrl)}`;
          }
        }

        // 큐 처리 (에러와 함께)
        processQueue(error);
        isRefreshing = false;

        return Promise.reject(error);
      } catch (refreshError) {
        // 세션 갱신 실패
        processQueue(error as AxiosError);
        isRefreshing = false;

        // 로그인 페이지로 리다이렉트
        if (!window.location.pathname.includes('login.php')) {
          window.location.href = '/pages/login.php';
        }

        return Promise.reject(refreshError);
      }
    }

    // 403 Forbidden 오류 처리
    if (error.response?.status === 403) {
      console.warn('[API] 접근 권한이 없습니다.');
      // 필요시 권한 오류 처리 로직 추가
    }

    // 500 서버 오류 처리
    if (error.response?.status === 500) {
      console.error('[API] 서버 오류가 발생했습니다.');
    }

    // 네트워크 오류 처리
    if (!error.response) {
      console.error('[API] 네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.');
    }

    return Promise.reject(error);
  }
);

/**
 * API 클라이언트 내보내기
 */
export default apiClient;

